/**
 * Utility functions for trait display names and descriptions
 * 
 * Note: We inline the mapping here to avoid circular dependency issues
 * with unit-columns.tsx. This mapping matches TRAIT_ENUM_VALUES in unit-columns.tsx
 */

import { SHOCK_TRAIT_RANGE_M } from '@/lib/damage-calculator/shock-trait-constants';

// Mapping: technical name -> display name
// This is the reverse of TRAIT_ENUM_VALUES in unit-columns.tsx
const TRAIT_DISPLAY_NAMES: Record<string, string> = {
  "_airlift": "Airlift",
  "_amphibie": "Amphibious",
  "_canBeAirlifted": "Can Be Airlifted",
  "_electronic_warfare": "Electronic Warfare",
  "_eo_dazzler": "EO Dazzler",
  "_era": "ERA",
  "_falseflag": "False Flag",
  "_fireDirection": "Fire Direction",
  "_gsr": "GSR",
  "_cbr": "Counter-Battery Radar",
  "_instructor": "Instructor",
  "_jammer": "Jammer",
  "_militia": "Militia",
  "_mountaineer": "Mountaineer",
  "_mp": "MP",
  "_para": "Para",
  "_resolute": "Resolute",
  "_security": "Security",
  "_sf": "Special Forces",
  "_singint": "SIGINT",
  "_smoke_launcher": "Smoke Launcher",
  "_sniper": "Sniper",
  "_transport1": "Transport",
  "_transport2": "Prime Mover",
  "_choc": "Shock",
  "_ifv": "IFV",
  "_leader": "Leader",
  "_reco": "Recon",
  "_reservist": "Reservist",
};

// Trait descriptions for tooltips
const TRAIT_DESCRIPTIONS: Record<string, string> = {
  "_choc": `Reduces aim time and reload time when enemy units are within ${SHOCK_TRAIT_RANGE_M}m`,
  "_sniper": "Increases stealth and damage",
  "_eo_dazzler": "Reduces accuracy of enemy missiles",
  "_electronic_warfare": "Always active - provides ECM bonuses",
  "_airlift": "Unit can be airlifted by transport aircraft",
  "_amphibie": "Can traverse water at reduced speed",
  "_canBeAirlifted": "Unit can be transported by airlift-capable units",
  "_era": "Explosive Reactive Armor - provides additional protection",
  "_falseflag": "Unit has low dangerousness, will be last unit auto-targeted",
  "_fireDirection": "Provides fire direction bonuses to nearby units",
  "_gsr": "Ground Surveillance Radar - improved detection",
  "_cbr": "This unit is equipped with a Counter-Battery Radar, designed to detect incoming artillery projectiles. Tracking their trajectories, it can locate the enemy weapon system's ground position. Counter-Battery Radar units feature an Area of Effect, which reveals an enemy artillery unit the moment they fire, regardless of the terrain. The location is lost once the enemy unit ceases firing.",
  "_instructor": "Removes reservist trait from units in area",
  "_jammer": "Electronic jamming capabilities",
  "_militia": "Militia unit with reduced effectiveness",
  "_mountaineer": "Improved movement in mountainous terrain",
  "_mp": "Military Police unit",
  "_para": "Access to advanced deployment",
  "_resolute": "Reduced suppression effects",
  "_security": "Security unit increased optics when stationary",
  "_sf": "Display-only indicator that this unit uses Special Forces veterancy progression. The actual stat bonuses are applied automatically through veterancy levels, not by toggling this trait.",
  "_singint": "Signals Intelligence capabilities",
  "_smoke_launcher": "Can deploy smoke screens",
  "_transport1": "Transport capacity for infantry",
  "_transport2": "A transport able to tow other units",
  "_ifv": "Infantry Fighting Vehicle - provides suppression reduction to nearby infantry",
  "_leader": "Command unit - provides veterancy bonuses to nearby units",
  "_reco": "Reconnaissance unit - improved optics and detection, access to advanced deployment",
  "_reservist": "Reservist unit - reduced effectiveness",
};

/**
 * Get the English display name for a trait
 * @param traitName - Technical trait name (e.g., "_choc")
 * @returns Display name (e.g., "Shock") or the original name if not found
 */
export function getTraitDisplayName(traitName: string): string {
  return TRAIT_DISPLAY_NAMES[traitName] || traitName;
}

/**
 * Get a description for a trait to display in tooltips
 * @param traitName - Technical trait name (e.g., "_choc")
 * @returns Description string or a default message if not found
 */
export function getTraitDescription(traitName: string): string {
  return TRAIT_DESCRIPTIONS[traitName] || "Trait effect";
}

